<template>
  <k-modal :show="showModal" @close="onClose" :id="`assign-reassign-episode-modal`" role="dialog">
    <template #header>
      <h3>{{ modalTitle }}</h3>
    </template>
    <template #body>
      <section class="episode-details-section">
        <h4 class="section-title">Episode Details</h4>
        <div class="table-container">
          <k-table
            :rows="kableRows"
            :headers="kableHeaders"
            :hideable="false"
            :panel="false"
            :show-controls="false"
          />
        </div>
      </section>
      <section class="mapping-section">
        <h4 class="section-title">Mapping</h4>
        <div v-if="programmeDropdownOptions.length > 0">
          <div class="dropdown-instructions">
            <div class="dropdown-text">
              <p>Use the dropdown to {{ isAssign ? 'assign' : 're-assign' }} the episode to the selected programme.</p>
              <p class="confirm-text">Click Confirm Changes to complete the {{ isAssign ? 'assignment' : 're-assignment' }}.</p>
            </div>
            <div class="dropdown-container">
              <k-dropdown
                id="programme-dropdown"
                :options="programmeDropdownOptions"
                v-model="selectedProgramme"
                :showSearch="false"
                placeholder="Select Programme"
                class="programme-dropdown"
              />
            </div>
          </div>
          <div class="mapping-container">
            <k-panel v-if="!isAssign && currentProgrammeData" :hasContentToggle="false" :title="currentProgrammeData.programme_name" icon="fa-diagram-project" class="programme-card">
              <template #body>
                <div>
                  <p>Product: <b>{{ currentProgrammeData.product_name }}</b></p>
                  <p>Access: <k-status-pill :type="currentProgrammeData.is_frozen ? 'danger' : 'success'" :status-value="getProgrammeAccess(currentProgrammeData)" /></p>
                  <p>Start date: <b>{{ formatProgrammeDate(currentProgrammeData.programme_start_date) }}</b></p>
                  <p>End date: <b>{{ formatProgrammeDate(currentProgrammeData.planned_end_date) }}</b></p>
                </div>
              </template>
            </k-panel>
            <k-panel v-else-if="isAssign" :hasContentToggle="false" class="programme-card">
              <template #body>
                <p class="no-selection-text"><i class="fas fa-link-slash" aria-hidden="true"></i> No linked programme</p>
              </template>
            </k-panel>
            <div class="mapping-arrow-container">
              <span class="mapping-arrow-text"><i class="fas fa-link" aria-hidden="true"></i> {{ isAssign ? 'Assign to' : 'Re-assign to' }}</span>
              <span class="mapping-arrow"><i class="fas fa-right-long arrow-icon" aria-hidden="true"></i></span>
            </div>
            <k-panel v-if="selectedProgrammeData" :hasContentToggle="false" :title="selectedProgrammeData.programme_name" icon="fa-diagram-project" class="programme-card">
              <template #body>
                <div>
                  <p>Product: <b>{{ selectedProgrammeData.product_name }}</b></p>
                  <p>Access: <k-status-pill :type="selectedProgrammeData.is_frozen ? 'danger' : 'success'" :status-value="getProgrammeAccess(selectedProgrammeData)" /></p>
                  <p>Start date: <b>{{ formatProgrammeDate(selectedProgrammeData.programme_start_date) }}</b></p>
                  <p>End date: <b>{{ formatProgrammeDate(selectedProgrammeData.planned_end_date) }}</b></p>
                </div>
              </template>
            </k-panel>
            <k-panel v-else :hasContentToggle="false" class="programme-card">
              <template #body>
                <p class="no-selection-text">No programme selected</p>
              </template>
            </k-panel>
          </div>
        </div>
        <div class="no-programmes-container" v-else>
          <p class="confirm-text"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> No valid programmes to assign this episode to.</p>
          <p>Learner is not a member of any other KATE programmes which match the LARS code of this episode.</p>
        </div>
      </section>
    </template>
    <template #footer>
      <div class="modal-footer-left">
        <button class="btn btn-outlined" @click="onClose">Cancel</button>
      </div>
      <div class="modal-footer-right">
        <button class="btn btn-success" @click="confirmAssign" :disabled="confirmDisabled">Confirm Changes</button>
      </div>
    </template>
  </k-modal>
</template>

<style>
#assign-reassign-episode-modal .modal-container {
  width: 80vw;
}
</style>

<style scoped>
.dropdown-instructions {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.no-programmes-container {
  margin-bottom: 20px;
}

.dropdown-text {
  width: 60%;
}

.dropdown-container {
  width: 40%;
}

.confirm-text {
  color: var(--kate-type-secondary) !important;
}

.mapping-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.programme-card {
  width: 40%;
  align-self: stretch;
  align-content: center;
  min-height: 220px;
}

.mapping-arrow-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.mapping-arrow-text {
  font-size: 1.2em;
  color: var(--kate-type-secondary);
}

.mapping-arrow {
  font-size: 3em;
}

.no-selection-text {
  text-align: center;
}

@media (max-width: 1050px) {
  .dropdown-instructions {
    flex-direction: column;
  }

  .dropdown-text,
  .dropdown-container {
    width: 100%;
  }

  .mapping-container {
    flex-direction: column;
  }

  .programme-card {
    width: 100%;
  }

  .arrow-icon {
    transform: rotate(90deg);
  }
}
</style>

<script>
import KTable from '@base-components/tables/display/k-table.vue';
import KModal from '@base-components/k-modal.vue';
import KDropdown from '@base-components/k-dropdown.vue';
import KPanel from '@base-components/k-panel.vue';
import TimeMixin from '@mixins/time-mixins';
import KStatusPill from '@base-components/k-status-pill.vue';

export default {
  components: {
    KModal,
    KTable,
    KDropdown,
    KPanel,
    KStatusPill,
  },

  mixins: [TimeMixin],

  props: {
    mode: {
      type: String,
      enum: ['assign', 'reassign'],
      required: true,
    },
    showModal: {
      type: Boolean,
      required: true,
    },
    episode: {
      type: Object,
      required: true,
    },
    programmes: {
      type: Array,
      required: true,
    },
    learnerId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      selectedProgramme: undefined,
      assigningEpisode: false,
    };
  },

  computed: {
    isAssign() {
      return this.mode === 'assign';
    },
    modalTitle() {
      return this.isAssign ? 'Assign Episode' : 'Re-Assign Episode';
    },

    ulnHeader() {
      return {
        uln: {
          name: 'ULN',
          type: 'string',
        },
      };
    },
    fullNameHeader() {
      return {
        full_name: {
          name: 'Learner Name',
          type: 'string',
        },
      };
    },
    emailHeader() {
      return {
        email: {
          name: 'Email',
          type: 'string',
        },
      };
    },
    programmeHeader() {
      return {
        programme: {
          name: 'Programme',
          type: 'string',
        },
      };
    },
    standardHeader() {
      return {
        standard: {
          name: 'Standard',
          type: 'string',
        },
      };
    },
    ilrStatusHeader() {
      return {
        ilr_status: {
          name: 'Status',
          type: 'string',
        },
      };
    },
    startDateHeader() {
      return {
        start_date: {
          name: 'Start Date',
          type: 'string',
        },
      };
    },
    endDateHeader() {
      return {
        end_date: {
          name: 'End Date',
          type: 'string',
        },
      };
    },
    kableHeaders() {
      if (this.isAssign) {
        return {
          ...this.ulnHeader,
          ...this.fullNameHeader,
          ...this.emailHeader,
          ...this.ilrStatusHeader,
          ...this.startDateHeader,
          ...this.endDateHeader,
        };
      }
      return {
        ...this.ulnHeader,
        ...this.fullNameHeader,
        ...this.programmeHeader,
        ...this.standardHeader,
        ...this.ilrStatusHeader,
        ...this.startDateHeader,
        ...this.endDateHeader,
      };
    },

    kableRows() {
      const rows = [{
        episode_id: this.episode.episode_id,
        uln: this.episode.uln,
        full_name: this.episode.full_name,
        email: this.episode.email,
        ilr_status: this.episode.ilr_status,
        start_date: this.episode.start_date,
        end_date: this.episode.end_date,
        standard: this.episode.standard,
        programme: this.episode.programme,
      }];
      return rows;
    },

    programmeDropdownOptions() {
      if (this.isAssign) {
        return this.episode.candidate_programmes.map(programme => ({
          id: programme.programme_id,
          name: programme.programme_name,
        }));
      }
      return this.programmes
        .filter(programme => programme.programme_member_id !== this.episode.programme_member_id
          && programme.ilr_standard_lars_code === this.episode.lars_code)
        .map(programme => ({
          id: programme.programme_id,
          name: programme.programme_name,
        }));
    },

    currentProgrammeData() {
      if (!this.isAssign) {
        return this.programmes.find(programme => programme.programme_member_id === this.episode.programme_member_id);
      }
      return undefined;
    },

    selectedProgrammeData() {
      return this.programmes.find(programme => programme.programme_id === this.selectedProgramme);
    },

    confirmDisabled() {
      return this.assigningEpisode || !this.selectedProgramme;
    },

  },

  methods: {
    unlinkEpisode() {
      this.$logger.info('Unlinking episode', { episodeId: this.episode.pics_id });
      return this.$http.delete(`/api/profile/ilr/user/${this.learnerId}/episodes/${this.episode.pics_id}/link`)
        .then(() => {
          this.$logger.info('Episode unlinked', { episodeId: this.episode.pics_id });
        })
        .catch(err => {
          this.$logger.error('Error unlinking episode', undefined, err);
          throw err;
        });
    },

    linkEpisode() {
      const pmid = this.selectedProgrammeData.programme_member_id;
      this.$logger.info('Linking episode', { episodeId: this.episode.pics_id });
      return this.$http.post(`/api/profile/ilr/user/${this.learnerId}/episodes/${this.episode.pics_id}/link`, { programme_member_id: pmid })
        .then(() => {
          this.$logger.info('Episode linked', { episodeId: this.episode.pics_id });
        })
        .catch(err => {
          this.$logger.error('Error linking episode', undefined, err);
          throw err;
        });
    },

    confirmAssign() {
      this.$ktoast.confirmToast(`Confirm that you want to ${this.isAssign ? 'assign' : 're-assign'} this episode.`, 'warning', () => {
        if (this.isAssign) {
          this.assignEpisode();
        } else {
          this.reassignEpisode();
        }
      });
    },

    async assignEpisode() {
      this.assigningEpisode = true;
      try {
        await this.linkEpisode();
        this.$emit('episode-assigned');
        this.onClose();
      } catch (err) {
        this.$logger.error('Error assigning episode', { episodeId: this.episode.pics_id }, err);
        this.$ktoast.showError(err);
      } finally {
        this.assigningEpisode = false;
      }
    },

    async reassignEpisode() {
      this.assigningEpisode = true;
      try {
        this.$logger.info('Re-assigning episode', { episodeId: this.episode.pics_id });
        await this.unlinkEpisode();
        await this.linkEpisode();
        this.$logger.info('Episode re-assigned', { episodeId: this.episode.pics_id });
        this.$ktoast.success('Episode re-assigned');
        this.$emit('episode-assigned');
        this.onClose();
      } catch (err) {
        this.$logger.error('Error re-assigning episode', { episodeId: this.episode.pics_id }, err);
        this.$ktoast.showError(err);
      } finally {
        this.assigningEpisode = false;
      }
    },

    onClose() {
      this.$emit('close');
      this.selectedProgramme = undefined;
      this.assigningEpisode = false;
    },

    formatProgrammeDate(date) {
      return this.formatDateString(date) || 'No date';
    },

    getProgrammeAccess(programme) {
      return programme.is_frozen ? 'Frozen' : 'Granted';
    },
  },
};
</script>
